import { useState } from 'react';
import {
  Card, DataTable, Badge, Button, Modal, TextField, Select,
  BlockStack, InlineStack, Text, Banner, Spinner, Box,
} from '@shopify/polaris';
import { useAdminUsers } from '../../hooks/useAdmin';
import type { AdminUser } from '../../api/admin';

const ROLE_OPTIONS = [
  { label: 'Support', value: 'support' },
  { label: 'Analyst', value: 'analyst' },
  { label: 'Super Admin', value: 'superadmin' },
];

interface CreateModalProps {
  open: boolean;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onCreate: (data: { name: string; email: string; password: string; role: string }) => Promise<AdminUser | null>;
}

function CreateModal({ open, loading, error, onClose, onCreate }: CreateModalProps) {
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [password, setPass]   = useState('');
  const [role, setRole]       = useState('support');
  const [errors, setErrors]   = useState<Record<string, string>>({});

  const handleSubmit = async () => {
    const e: typeof errors = {};
    if (!name.trim())     e.name     = 'Name is required.';
    if (!email.trim())    e.email    = 'Email is required.';
    if (password.length < 12) e.password = 'Minimum 12 characters.';
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});

    const result = await onCreate({ name: name.trim(), email: email.trim(), password, role });
    if (result) { setName(''); setEmail(''); setPass(''); setRole('support'); onClose(); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Create Admin User"
      primaryAction={{ content: 'Create', loading, onAction: handleSubmit }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}>
      <Modal.Section>
        <BlockStack gap="400">
          {error && <Banner tone="critical">{error}</Banner>}
          <TextField label="Name" value={name} onChange={setName} autoComplete="off" error={errors.name} />
          <TextField label="Email" type="email" value={email} onChange={setEmail} autoComplete="off" error={errors.email} />
          <TextField label="Password (min 12 chars)" type="password" value={password} onChange={setPass} autoComplete="new-password" error={errors.password} />
          <Select label="Role" options={ROLE_OPTIONS} value={role} onChange={setRole} />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

interface Props {
  currentAdminId: number;
}

export function AdminUsersPanel({ currentAdminId }: Props) {
  const { users, loading, error, create, remove } = useAdminUsers();
  const [showCreate, setShowCreate] = useState(false);

  const rows = users.map((u) => [
    u.id,
    u.name,
    u.email,
    <Badge key={u.id} tone={u.role === 'superadmin' ? 'success' : 'info'}>{u.role_label}</Badge>,
    <Badge key={u.id + 's'} tone={u.is_active ? 'success' : 'critical'}>{u.is_active ? 'Active' : 'Inactive'}</Badge>,
    u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never',
    <Button
      key={u.id + 'd'}
      size="slim"
      tone="critical"
      disabled={u.id === currentAdminId}
      onClick={() => remove(u.id)}
    >
      Delete
    </Button>,
  ]);

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">Admin Users</Text>
          <Button variant="primary" onClick={() => setShowCreate(true)}>Add Admin</Button>
        </InlineStack>

        {error && <Banner tone="critical">{error}</Banner>}

        {loading ? (
          <Box padding="600"><InlineStack align="center"><Spinner /></InlineStack></Box>
        ) : (
          <DataTable
            columnContentTypes={['numeric', 'text', 'text', 'text', 'text', 'text', 'text']}
            headings={['ID', 'Name', 'Email', 'Role', 'Status', 'Last Login', 'Actions']}
            rows={rows}
          />
        )}
      </BlockStack>

      <CreateModal
        open={showCreate}
        loading={loading}
        error={error}
        onClose={() => setShowCreate(false)}
        onCreate={create}
      />
    </Card>
  );
}
